import axios from "axios";
import { sendPatch } from "./utils";
import type { CompanyId, PortalId } from "./companies";
import type { ProjectId } from "./projects";
import type { PortalStage, PriorityId, SlaClock, TicketId } from "./tickets";
import type { UserId } from "./users";

export type SupportBucket =
    "Breached" | "AtRisk" | "Unassigned" | "NeedsReply" | "Mine" | "AwaitingFirstReply";

export type SupportQueueClock = SlaClock & {
    /** Business minutes left on a running clock when the queue was fetched; null once it stops. */
    remainingMinutes: number | null;
};

export type SupportQueueRow = {
    ticketId: TicketId;
    key: string;
    title: string;
    projectId: ProjectId;
    projectName: string;
    companyName: string | null;
    requester: string | null;
    assignedToId: UserId | null;
    assignedTo: string | null;
    priorityName: string;
    priorityIconColor: string | null;
    stage: PortalStage;
    resolved: boolean;
    resolutionName: string | null;
    created: string;
    lastPublicReply: string | null;
    clocks: SupportQueueClock[];
    buckets: SupportBucket[];
};

export type SupportQueue = {
    counts: Record<SupportBucket, number>;
    total: number;
    rows: SupportQueueRow[];
};

export type SupportPortalLink = {
    id: PortalId;
    name: string;
    slug: string;
};

export type SupportProjectLink = {
    id: ProjectId;
    name: string;
};

export type SupportContact = {
    userId: UserId;
    name: string;
    email: string;
    companyId: CompanyId;
    companyName: string;
    portalId: PortalId;
    portalName: string;
};

export type SupportCompanyLink = {
    id: CompanyId;
    name: string;
    portalId: PortalId;
    portalName: string;
    /** The picker preselects a company whose email domains match the new contact's address. */
    domainMatch: boolean;
};

export type SupportRequestField = {
    id: string;
    name: string;
    fieldName: string;
    type: string;
    rules: string | null;
    options: { id: string; title: string; value: string }[];
};

export type SupportNewContact = {
    email: string;
    displayName: string | null;
    companyId: CompanyId;
};

export type SupportCreateRequest = {
    /** Exactly one of this or newContact names the reporter. */
    onBehalfOfId: UserId | null;
    /** Files for someone with no account yet; the account is created with the request. */
    newContact: SupportNewContact | null;
    /** Which of an existing contact's companies files the request; null takes the first one able to. */
    companyId: CompanyId | null;
    title: string;
    description: string | null;
    priorityId: PriorityId | null;
    attachmentIds: string[];
    customData: { fieldName: string; value: string | null }[];
    ccEmails: string[];
    silenceCustomer: boolean;
};

export type SupportRequestCreated = {
    id: TicketId;
    key: string;
};

/**
 * All: every portal the caller can see. Agent: only portals whose queue lists the caller.
 * Filing: only portals with a project the caller can file requests in.
 */
export type SupportScope = "All" | "Agent" | "Filing";

export type SupportQueueFilter = {
    scope?: SupportScope;
    portal?: PortalId | null;
    project?: ProjectId | null;
    priority?: PriorityId | null;
    /** "me", "none", or a user id. */
    assignee?: string | null;
    company?: CompanyId | null;
    bucket?: SupportBucket | null;
    text?: string;
    includeResolved?: boolean;
    page?: number;
    perPage?: number;
};

export interface SupportApi {
    getQueue(filter: SupportQueueFilter): Promise<SupportQueue>;
    getPortals(scope?: SupportScope): Promise<SupportPortalLink[]>;
    getProjects(scope?: SupportScope): Promise<SupportProjectLink[]>;
    getContacts(text?: string, company?: CompanyId): Promise<SupportContact[]>;
    getContactProjects(user: UserId): Promise<SupportProjectLink[]>;
    getCompanies(text?: string, email?: string): Promise<SupportCompanyLink[]>;
    createCompany(portal: PortalId, name: string): Promise<SupportCompanyLink>;
    getCompanyProjects(company: CompanyId): Promise<SupportProjectLink[]>;
    getRequestFields(project: ProjectId): Promise<SupportRequestField[]>;
    createRequest(
        project: ProjectId,
        request: SupportCreateRequest
    ): Promise<SupportRequestCreated>;
    assign(ticket: TicketId, user: UserId): Promise<void>;
}

export const supportApi: SupportApi = {
    async getQueue(filter) {
        const { data } = await axios.get<SupportQueue>("/api/v0/support/queue", {
            params: {
                scope: filter.scope,
                portal: filter.portal ?? undefined,
                project: filter.project ?? undefined,
                priority: filter.priority ?? undefined,
                assignee: filter.assignee ?? undefined,
                company: filter.company ?? undefined,
                bucket: filter.bucket ?? undefined,
                text: filter.text || undefined,
                includeResolved: filter.includeResolved || undefined,
                page: filter.page || undefined,
                perPage: filter.perPage || undefined,
            },
        });
        return data;
    },
    async getPortals(scope) {
        const { data } = await axios.get<SupportPortalLink[]>("/api/v0/support/portals", {
            params: { scope },
        });
        return data;
    },
    async getProjects(scope) {
        const { data } = await axios.get<SupportProjectLink[]>("/api/v0/support/projects", {
            params: { scope },
        });
        return data;
    },
    async getContacts(text, company) {
        const { data } = await axios.get<SupportContact[]>("/api/v0/support/contacts", {
            params: { text: text || undefined, company: company || undefined },
        });
        return data;
    },
    async getContactProjects(user) {
        const { data } = await axios.get<SupportProjectLink[]>(
            `/api/v0/support/contacts/${user}/projects`
        );
        return data;
    },
    async getCompanies(text, email) {
        const { data } = await axios.get<SupportCompanyLink[]>("/api/v0/support/companies", {
            params: { text: text || undefined, email: email || undefined },
        });
        return data;
    },
    async createCompany(portal, name) {
        const { data } = await axios.post<SupportCompanyLink>("/api/v0/support/companies", {
            portalId: portal,
            name,
        });
        return data;
    },
    async getCompanyProjects(company) {
        const { data } = await axios.get<SupportProjectLink[]>(
            `/api/v0/support/companies/${company}/projects`
        );
        return data;
    },
    async getRequestFields(project) {
        const { data } = await axios.get<SupportRequestField[]>(
            `/api/v0/support/projects/${project}/request-fields`
        );
        return data;
    },
    async createRequest(project, request) {
        const { data } = await axios.post<SupportRequestCreated>(
            `/api/v0/support/projects/${project}/requests`,
            request
        );
        return data;
    },
    async assign(ticket, user) {
        await sendPatch(`/api/v0/tickets/${ticket}`, [
            { op: "replace", path: "/assignedToId", value: user },
        ]);
    },
};
